/**
 * @pj/script — the event/effect layer. Content authors attach `Effect[]` to
 * interactables (treasure chests, signs, doors, …); the simulation runs them
 * through a capability context. This is the decomp's "scripts call into every
 * system" seam, kept open: a new effect is one variant + one handler, and the
 * context is injected by the host so the script layer never imports
 * battle/party/etc.
 *
 * Today this is a flat, declarative effect list (enough for treasure hunts and
 * dialogue). A full imperative VM (parallel movement, cutscene sequencing,
 * call/return) layers on top later without changing this interface.
 */

export type Effect =
  /** One Gen-3 message box (≤ ~2 short lines). Consecutive dialogue effects
   *  queue as ▼-advanced boxes, exactly like chained msgbox script beats. */
  | { kind: "dialogue"; lines: string[] }
  | { kind: "toast"; text: string }
  | { kind: "setFlag"; flag: string }
  | { kind: "grantAbility"; ability: string }
  /** Put a (session) item in the bag — key items for door gating. */
  | { kind: "giveItem"; item: string }
  | { kind: "addExp"; amount: number };

/** The capabilities effects may call. The host (simulation) provides these,
 *  so adding a new system means widening this interface + the host impl. */
export interface EffectContext {
  dialogue(lines: string[]): void;
  toast(text: string): void;
  setFlag(flag: string): void;
  grantAbility(ability: string): void;
  giveItem(item: string): void;
  addExp(amount: number): void;
}

export function runEffects(effects: readonly Effect[], ctx: EffectContext): void {
  for (const e of effects) runEffect(e, ctx);
}

export function runEffect(e: Effect, ctx: EffectContext): void {
  switch (e.kind) {
    case "dialogue":
      ctx.dialogue(e.lines);
      return;
    case "toast":
      ctx.toast(e.text);
      return;
    case "setFlag":
      ctx.setFlag(e.flag);
      return;
    case "grantAbility":
      ctx.grantAbility(e.ability);
      return;
    case "giveItem":
      ctx.giveItem(e.item);
      return;
    case "addExp":
      ctx.addExp(e.amount);
      return;
  }
}
